import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from "@mui/material";
import { useCallback, useState } from "react";
import {
  QuoteAgentOffboardingRequestStatus,
  useOffboardCompanyFromQuoteAgentMutation,
} from "../../generated/graphql";
import { SNACKBAR_ANCHOR } from "./company-configurations-utils";

type OffboardFromQuoteAgentProps = {
  readonly companyUuid: string;
  readonly companyName: string;
};

const REFUSAL_CODES = new Set(["BAD_REQUEST", "NOT_FOUND"]);

const successMessageFor = (
  status: QuoteAgentOffboardingRequestStatus,
  companyName: string,
): string =>
  status === QuoteAgentOffboardingRequestStatus.AlreadyQueued
    ? `${companyName} is already queued for offboarding from the quote agent`
    : `${companyName} is queued for offboarding from the quote agent`;

/**
 * Staff action that erases a company from the quote agent. The confirmation
 * stays open on failure, showing the error, so the same click retries. A
 * refusal the backend states (a 4xx) is shown without a retry, since repeating
 * the request cannot change the answer.
 */
const OffboardFromQuoteAgent = ({
  companyUuid,
  companyName,
}: OffboardFromQuoteAgentProps) => {
  const [offboard, { loading }] = useOffboardCompanyFromQuoteAgentMutation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);

  const handleOpen = useCallback(() => {
    setError(null);
    setRetryable(true);
    setOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    if (!loading) {
      setOpen(false);
    }
  }, [loading]);

  const handleCloseSuccess = useCallback(() => {
    setSuccess(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    setError(null);
    try {
      const response = await offboard({
        variables: { input: { companyUuid } },
      });
      const status = response.data?.offboardCompanyFromQuoteAgent.status;
      const errors = response.errors ?? [];
      const messages = errors.map((err) => err.message);
      if (status === undefined || messages.length > 0) {
        setRetryable(
          !(
            errors.length > 0 &&
            errors.every((err) => {
              const code = err.extensions?.code;
              return typeof code === "string" && REFUSAL_CODES.has(code);
            })
          ),
        );
        setError(
          messages.length > 0
            ? messages.join(", ")
            : "The offboarding was not queued",
        );
        return;
      }
      setOpen(false);
      setSuccess(successMessageFor(status, companyName));
    } catch (error_) {
      setError(
        `The offboarding was not queued: ${
          error_ instanceof Error ? error_.message : String(error_)
        }`,
      );
    }
  }, [companyName, companyUuid, offboard]);

  return (
    <>
      <Button color="error" variant="outlined" onClick={handleOpen}>
        Offboard from quote agent
      </Button>
      <Dialog open={open} onClose={handleCancel}>
        <DialogTitle>{`Offboard ${companyName} from the quote agent?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`This permanently erases ${companyName}'s quote conversations, messages, stored email bodies and forwarding address from the quote agent. It cannot be undone.`}
          </DialogContentText>
          <DialogContentText>
            For 30 days afterwards the quote agent refuses this company&apos;s
            mail and data, so the quote agent cannot be set up for it again
            before then.
          </DialogContentText>
          <DialogContentText>
            Turn the quote agent off for this company first; the request is
            refused while it is on.
          </DialogContentText>
          {error === null ? null : <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={handleCancel}>
            Cancel
          </Button>
          {retryable ? (
            <Button
              color="error"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
              variant="contained"
              onClick={handleConfirm}
            >
              {error === null ? "Offboard" : "Retry"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
      <Snackbar anchorOrigin={SNACKBAR_ANCHOR} open={success !== null}>
        <Alert severity="success" onClose={handleCloseSuccess}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};
OffboardFromQuoteAgent.displayName = "OffboardFromQuoteAgent";

export default OffboardFromQuoteAgent;
